import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { ModuleId } from '@/types'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { LaptopFrame, PhoneFrame } from '@/components/landing/device-frames'
import { LaptopScreen } from '@/components/landing/laptop-screens'
import { PhoneScreen } from '@/components/landing/phone-screens'
import { MODULE_ICONS } from '@/components/landing/module-icons'
import { BadgeCheck, Check, Flag, MousePointerClick } from 'lucide-react'

import {
    AnimatePresence,
    motion,
    useInView,
    useReducedMotion,
    useScroll,
    useSpring,
    useTransform
} from 'framer-motion'

import { MODULES } from '@/constants/campus'

interface ProductShowcaseProps {
    active: ModuleId
    autoPlay: boolean
    onSelect: (id: ModuleId) => void
    onAutoAdvance: () => void
}

export const ProductShowcase: FC<ProductShowcaseProps> = ({ active, autoPlay, onSelect, onAutoAdvance }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const deviceRef = useRef<HTMLDivElement>(null)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const [hovered, setHovered] = useState(false)
    const reduceMotion = useReducedMotion()
    const inView = useInView(sectionRef, { margin: '-20% 0px -20% 0px' })

    const { scrollYProgress } = useScroll({ target: deviceRef, offset: ['start 95%', 'start 18%'] })
    const spring = { stiffness: 110, damping: 26, mass: 0.6 }
    const rotateX = useSpring(useTransform(scrollYProgress, [0, 1], [24, 0]), spring)
    const scale = useSpring(useTransform(scrollYProgress, [0, 1], [0.9, 1]), spring)
    const lift = useSpring(useTransform(scrollYProgress, [0, 1], [60, 0]), spring)
    const phoneY = useSpring(useTransform(scrollYProgress, [0, 1], [110, 0]), spring)
    const phoneRotate = useSpring(useTransform(scrollYProgress, [0, 1], [8, -3]), spring)

    const activeModule = MODULES.find(module => module.id === active) ?? MODULES[0]
    const paused = hovered || !inView
    const showProgress = autoPlay && !reduceMotion

    const onTabKey = (event: KeyboardEvent<HTMLDivElement>) => {
        const index = MODULES.findIndex(module => module.id === active)
        let next = -1

        if (event.key === 'ArrowRight') next = (index + 1) % MODULES.length
        if (event.key === 'ArrowLeft') next = (index - 1 + MODULES.length) % MODULES.length
        if (event.key === 'Home') next = 0
        if (event.key === 'End') next = MODULES.length - 1
        if (next < 0) return

        event.preventDefault()
        onSelect(MODULES[next].id)
        tabRefs.current[next]?.focus()
    }

    return (
        <section ref={sectionRef} id='modules' aria-label='Interactive product showcase' className='relative z-10 scroll-mt-24'>
            <div className='relative mx-auto mt-16 max-w-6xl px-4 sm:mt-20 sm:px-6'>
                <div aria-hidden='true' className='absolute bottom-0 left-1/2 top-[56%] w-screen -translate-x-1/2 bg-white' />

                <div
                    ref={deviceRef}
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    className='relative [perspective:2200px]'
                >
                    <motion.div
                        style={reduceMotion ? undefined : { rotateX, scale, y: lift }}
                        className='relative origin-[50%_85%]'
                    >
                        <div className='absolute -top-4 left-3 z-30 inline-flex items-center gap-2 rounded-full bg-[#0F2347]/90 px-3 py-1.5 text-[11px] font-semibold text-blue-200 ring-1 ring-blue-400/40 backdrop-blur sm:-top-5 sm:left-8 sm:text-xs'>
                            <span className='relative flex size-2'>
                                <span className='absolute inline-flex size-full animate-ping rounded-full bg-blue-400 opacity-75' />
                                <span className='relative inline-flex size-2 rounded-full bg-blue-500' />
                            </span>

                            Live Interactive Demo

                            <span className='hidden items-center gap-1 text-blue-300/80 sm:inline-flex'>
                                <MousePointerClick className='size-3.5' />
                                click around
                            </span>
                        </div>

                        <div aria-hidden='true' className='absolute -inset-x-10 -bottom-10 top-1/3 rounded-[50%] bg-indigo-500/20 blur-3xl' />

                        <div aria-hidden='true' className='relative mx-auto w-[94%] sm:w-[88%]'>
                            <LaptopFrame>
                                <LaptopScreen active={active} onSelect={onSelect} />
                            </LaptopFrame>
                        </div>

                        <motion.div
                            aria-hidden='true'
                            style={reduceMotion ? undefined : { y: phoneY, rotate: phoneRotate }}
                            className='absolute -bottom-[7%] right-0 z-20 w-[27%] sm:right-[2%] sm:w-[20%] lg:w-[18%]'
                        >
                            <PhoneFrame>
                                <PhoneScreen active={active} onSelect={onSelect} />
                            </PhoneFrame>
                        </motion.div>

                        <div aria-hidden='true' className='absolute -right-4 top-[10%] z-20 hidden w-60 animate-float rounded-2xl border border-white/10 bg-midnight-card/90 p-4 text-white shadow-2xl backdrop-blur-xl xl:block'>
                            <div className='flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-amber-300'>
                                <Flag className='size-3.5' />
                                32-Month Milestone
                            </div>

                            <p className='mt-2 text-sm font-semibold leading-snug'>
                                Month 18 · Interdisciplinary Team Challenge
                            </p>

                            <div className='mt-3 h-1.5 overflow-hidden rounded-full bg-white/10'>
                                <div className='h-full w-[78%] rounded-full bg-gradient-to-r from-amber-400 to-rose-400' />
                            </div>
                        </div>

                        <div aria-hidden='true' className='absolute -left-6 bottom-[16%] z-20 hidden w-64 animate-float rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_30px_60px_-24px_rgba(15,23,42,0.45)] [animation-delay:-3s] xl:block'>
                            <div className='flex items-center gap-3'>
                                <span className='grid size-10 place-items-center rounded-xl bg-emerald-50 text-emerald-600'>
                                    <BadgeCheck className='size-5' />
                                </span>

                                <div>
                                    <p className='text-sm font-bold text-ink'>Pass verified</p>
                                    <p className='text-xs text-slate-500'>T&P Cell · portal unlocked</p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>

            <div className='relative bg-white px-5 pb-20 pt-16 sm:pb-24 sm:pt-24'>
                <div className='mx-auto max-w-6xl text-center'>
                    <div
                        role='tablist'
                        aria-label='Platform modules'
                        onKeyDown={onTabKey}
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                        className='no-scrollbar mx-auto flex max-w-full gap-1 overflow-x-auto rounded-full border border-slate-200 bg-white p-1.5 shadow-[0_10px_30px_-18px_rgba(15,23,42,0.35)] sm:inline-flex'
                    >
                        {MODULES.map((module, index) => {
                            const Icon = MODULE_ICONS[module.id]
                            const selected = module.id === active

                            return (
                                <button
                                    key={module.id}
                                    ref={node => {
                                        tabRefs.current[index] = node
                                    }}
                                    id={`module-tab-${module.id}`}
                                    type='button'
                                    role='tab'
                                    aria-selected={selected}
                                    aria-controls='module-panel'
                                    tabIndex={selected ? 0 : -1}
                                    onClick={() => onSelect(module.id)}
                                    className={cn(
                                        'relative flex shrink-0 items-center gap-2 overflow-hidden rounded-full px-4 py-2.5 text-sm transition-colors sm:px-5',
                                        selected ? 'font-bold text-rose-600' : 'font-semibold text-slate-500 hover:text-ink'
                                    )}
                                >
                                    {selected && (
                                        <motion.span
                                            layoutId='module-pill'
                                            transition={{ type: 'spring', stiffness: 420, damping: 36 }}
                                            className='absolute inset-0 rounded-full bg-rose-50 ring-1 ring-rose-100'
                                        />
                                    )}

                                    <Icon className='relative size-4' />

                                    <span className='relative whitespace-nowrap'>
                                        {module.label}
                                    </span>

                                    {selected && showProgress && (
                                        <span
                                            key={`${module.id}-progress`}
                                            aria-hidden='true'
                                            onAnimationEnd={onAutoAdvance}
                                            style={{ animationPlayState: paused ? 'paused' : 'running' }}
                                            className='absolute inset-x-5 bottom-1 h-0.5 origin-left animate-progress-x rounded-full bg-rose-400/70'
                                        />
                                    )}
                                </button>
                            )
                        })}
                    </div>

                    <div id='module-panel' role='tabpanel' aria-labelledby={`module-tab-${active}`} className='mt-10 min-h-[220px] sm:mt-12'>
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.div
                                key={active}
                                initial={{ opacity: 0, y: 18, filter: 'blur(8px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                exit={{ opacity: 0, y: -12, filter: 'blur(6px)' }}
                                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <h2 className='mx-auto max-w-4xl text-balance text-3xl font-extrabold leading-[1.12] tracking-tight text-ink sm:text-5xl'>
                                    {activeModule.headline}
                                </h2>

                                <p className='mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg'>
                                    {activeModule.description}
                                </p>

                                <ul className='mt-7 flex flex-wrap items-center justify-center gap-2.5'>
                                    {activeModule.points.map(point => (
                                        <li key={point} className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-alabaster px-4 py-2 text-sm font-semibold text-slate-700'>
                                            <Check className='size-4 text-emerald-500' strokeWidth={3} />
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </section>
    )
}
